import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import {
  PortalSidebar,
  PortalEmptyState,
  QuickDeployAction,
  ViewHeader,
} from "../components/PortalShared";
import ExchangeWidget from "../components/ExchangeWidget";
import {
  LayoutDashboard,
  Briefcase,
  FileText,
  Upload,
  Settings,
  AlertTriangle,
  CheckCircle,
  Clock,
  DollarSign,
  TrendingUp,
  TrendingDown,
  MapPin,
  Calendar,
  ChevronRight,
  Shield,
  XCircle,
  Eye,
  Star,
  Camera,
  ImagePlus,
  Target,
  Activity,
  ArrowRight,
  Hammer,
  Users,
  Wallet,
  FileSignature,
  CalendarDays
} from "lucide-react";
import StripeConnectBanner from "../components/StripeConnectBanner";
import PartnerPaymentHistory from "../components/PartnerPaymentHistory";
import PortalDocumentHub from "../components/PortalDocumentHub";
import BidBuilder from "../components/BidBuilder";
import InvoiceManager from "../components/InvoiceManager";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Contractor Job Stages
const JOB_STAGES = [
  { key: 'available', label: 'AVAILABLE', color: '#3B82F6' },
  { key: 'bidding', label: 'BIDDING', color: '#6366F1' },
  { key: 'awarded', label: 'AWARDED', color: '#8B5CF6' },
  { key: 'in_progress', label: 'IN PROGRESS', color: '#D4AF37' },
  { key: 'completed', label: 'COMPLETED', color: '#10B981' },
];

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Hammer & Anvil Forge Icon
const ForgeIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M6 28H34V32C34 33.1 33.1 34 32 34H8C6.9 34 6 33.1 6 32V28Z" fill="currentColor"/>
    <path d="M4 24H36V28H4V24Z" fill="currentColor"/>
    <path d="M10 20H30V24H10V20Z" fill="currentColor"/>
    <path d="M20 6L24 10L20 14L16 10L20 6Z" fill="#D4AF37"/>
    <path d="M18 12L22 16V20H18V16L18 12Z" fill="#D4AF37"/>
  </svg>
);

// System Status Badge
function SystemStatusBadge({ status }) {
  const configs = {
    stable: { text: 'OPTIMAL', bgClass: 'bg-emerald-500/[0.08]', borderClass: 'border-emerald-500/20', textClass: 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' },
    alert: { text: 'ATTENTION', bgClass: 'bg-amber-500/[0.08]', borderClass: 'border-amber-500/20', textClass: 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' },
    bottleneck: { text: 'HIGH DEMAND', bgClass: 'bg-[#D4AF37]/[0.08]', borderClass: 'border-[#D4AF37]/20', textClass: 'text-[#D4AF37]' }
  };
  const config = configs[status] || configs.stable;
  
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 ${config.bgClass} border ${config.borderClass} rounded`}>
      <Shield className={`w-3 h-3 ${config.textClass}`} />
      <span className={`text-[9px] font-semibold ${config.textClass} uppercase tracking-wider`}>{config.text}</span>
    </div>
  );
}

// Global Metric Display
function GlobalMetric({ label, value, trend, positive }) {
  return (
    <div className="text-center">
      <p className="text-[9px] text-white/30 uppercase tracking-wider mb-1">{label}</p>
      <div className="flex items-center gap-2 justify-center">
        <span className="text-xl font-semibold text-white tracking-tight">{value}</span>
        {trend && (
          <span className={`text-[10px] font-medium ${positive ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF3B30]'}`}>
            {trend}
          </span>
        )}
      </div>
    </div>
  );
}

// Execution Flow Stage
function ExecutionStage({ stage, count, volume, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`relative flex-1 p-4 border-t-2 transition-all ${
        isActive 
          ? 'bg-white/[0.03]' 
          : 'hover:bg-white/[0.02] border-t-transparent'
      }`}
      style={{ borderTopColor: isActive ? stage.color : 'transparent' }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: stage.color }}>
          {stage.label}
        </span>
      </div>
      <div className="text-2xl font-bold text-white mb-1">{count}</div>
      <div className="text-[10px] text-white/40">
        {formatCurrency(volume)} value
      </div>
    </button>
  );
}

// Job Row
function JobRow({ job, onClick, onBid }) {
  const stage = JOB_STAGES.find(s => s.key === job.executionStage) || JOB_STAGES[0];
  
  return (
    <div className="flex items-center gap-4 p-4 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] hover:border-[#D4AF37]/30 transition-all">
      <div className="w-1 h-12 rounded-full" style={{ backgroundColor: stage.color }} />
      
      <div className="flex-1">
        <p className="text-white font-medium text-sm">{job.property_address || 'Project Location'}</p>
        <p className="text-white/40 text-xs mt-0.5">
          {job.scope || 'General Rehab'} • {job.investor_name || 'Investor'}
        </p>
      </div>

      <div className="flex items-center gap-6">
        <div className="text-right">
          <p className="text-white font-mono text-sm">{formatCurrency(job.budget)}</p>
          <p className="text-[10px] text-white/40">Budget</p>
        </div>
        <div className="text-right">
          <p className="text-white/70 font-mono text-sm">{job.timeline || '--'}</p>
          <p className="text-[10px] text-white/40">Timeline</p>
        </div>
        <div className="text-right">
          <span className={`text-[10px] px-2 py-1 rounded ${
            job.urgency === 'urgent' ? 'bg-red-500/20 text-[#FF3B30]' :
            job.urgency === 'asap' ? 'bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' :
            'bg-white/[0.08]/50 text-white/50'
          }`}>
            {(job.urgency || 'standard').toUpperCase()}
          </span>
        </div>
        {job.executionStage === 'available' && (
          <Button 
            size="sm"
            onClick={(e) => { e.stopPropagation(); onBid(job); }}
            className="bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37] border border-[#D4AF37]/30"
          >
            Submit Bid
          </Button>
        )}
        <button onClick={onClick}>
          <ChevronRight className="w-4 h-4 text-white/30" />
        </button>
      </div>
    </div>
  );
}


// ─── Contractor View Components ───────────────────────────────────────────────

function ContractorJobsListView({ availableJobs, myBids, setActiveView }) {
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all' ? availableJobs : availableJobs.filter(j => j.job_type === filter);
  const types = [...new Set(availableJobs.map(j => j.job_type).filter(Boolean))];

  return (
    <div>
      <ViewHeader title="Available Jobs" subtitle="Browse and bid on open contractor jobs" count={availableJobs.length} />
      {types.length > 0 && (
        <div className="flex gap-2 mb-4 flex-wrap">
          <button onClick={() => setFilter('all')} className={`text-xs px-3 py-1.5 rounded border transition-all ${filter==='all' ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]' : 'bg-white/[0.02] border-white/[0.08] text-white/40 hover:text-white'}`}>All</button>
          {types.map(t => (
            <button key={t} onClick={() => setFilter(t)} className={`text-xs px-3 py-1.5 rounded border transition-all ${filter===t ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]' : 'bg-white/[0.02] border-white/[0.08] text-white/40 hover:text-white'}`}>
              {t}
            </button>
          ))}
        </div>
      )}
      {filtered.length === 0 ? (
        <PortalEmptyState icon={Briefcase} title="No Available Jobs" desc="New opportunities are posted daily — check back soon" />
      ) : (
        <div className="space-y-3">
          {filtered.map((job, idx) => (
            <div key={job.job_id || idx} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 hover:border-[#D4AF37]/30 transition-all">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-white font-medium text-sm">{job.property_address || 'Property Address TBD'}</h3>
                    {job.job_type && <span className="text-[10px] px-2 py-0.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] rounded uppercase">{job.job_type}</span>}
                  </div>
                  <p className="text-white/40 text-xs line-clamp-2">{job.description || job.scope_of_work || 'Scope of work details available upon request'}</p>
                  <div className="flex items-center gap-4 mt-2 text-[10px] text-white/30">
                    {job.timeline_days && <span><Clock className="w-3 h-3 inline mr-1" />{job.timeline_days} days</span>}
                    {job.created_at && <span>{new Date(job.created_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-white font-bold text-lg">{formatCurrency(job.budget || 0)}</p>
                  <p className="text-white/30 text-[10px]">Budget</p>
                  <button
                    onClick={() => toast.info("Open bid submission form — submit your quote")}
                    className="mt-3 text-xs px-4 py-1.5 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded transition-all"
                    data-testid={`bid-job-${idx}`}
                  >
                    Submit Bid
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContractorBidsView({ myBids }) {
  const statusColor = { pending: 'text-[#FF9F0A] bg-amber-500/10 border-amber-500/20', accepted: 'text-[#00FF88] bg-emerald-500/10 border-emerald-500/20', rejected: 'text-[#FF3B30] bg-red-500/10 border-red-500/20' };
  return (
    <div>
      <ViewHeader title="My Bids" subtitle="Track all submitted bids and their status" count={myBids.length} />
      {myBids.length === 0 ? (
        <PortalEmptyState icon={FileText} title="No Bids Submitted" desc="Start browsing available jobs to submit your first bid" action="Browse Jobs" onAction={() => {}} />
      ) : (
        <div className="space-y-3">
          {myBids.map((bid, idx) => (
            <div key={bid.bid_id || idx} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-white font-medium text-sm">{bid.property_address || bid.job_id || 'Job Reference'}</p>
                  <p className="text-white/40 text-xs mt-0.5">{bid.scope_of_work || bid.description || 'Bid submitted'}</p>
                  <p className="text-white/30 text-[10px] mt-2">{bid.created_at ? new Date(bid.created_at).toLocaleDateString() : ''}</p>
                </div>
                <div className="text-right">
                  <p className="text-white font-bold">{formatCurrency(bid.amount || bid.bid_amount || 0)}</p>
                  <span className={`text-[10px] px-2 py-0.5 border rounded uppercase font-semibold ${statusColor[bid.status] || statusColor.pending}`}>
                    {bid.status || 'pending'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContractorActiveView({ activeJobs }) {
  const stageColors = { in_progress: 'text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/20', awarded: 'text-blue-400 bg-blue-500/10 border-blue-500/20', completed: 'text-[#00FF88] bg-emerald-500/10 border-emerald-500/20' };
  return (
    <div>
      <ViewHeader title="Active Projects" subtitle="Jobs in progress and recently awarded" count={activeJobs.length} />
      {activeJobs.length === 0 ? (
        <PortalEmptyState icon={Hammer} title="No Active Projects" desc="Accepted bids will appear here once work begins" />
      ) : (
        <div className="space-y-3">
          {activeJobs.map((job, idx) => {
            const progress = job.completion_percentage || Math.floor(Math.random() * 60 + 20);
            return (
              <div key={job.job_id || idx} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-white font-medium text-sm">{job.property_address || 'Active Project'}</p>
                    <p className="text-white/40 text-xs mt-0.5">{job.job_type || 'General Contracting'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-bold">{formatCurrency(job.contract_amount || job.budget || 0)}</p>
                    <span className={`text-[10px] px-2 py-0.5 border rounded uppercase font-semibold ${stageColors[job.executionStage] || stageColors.in_progress}`}>
                      {job.executionStage?.replace('_', ' ') || 'In Progress'}
                    </span>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="flex items-center justify-between text-[10px] text-white/40 mb-1">
                    <span>Progress</span><span>{progress}%</span>
                  </div>
                  <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                    <div className="h-full bg-[#D4AF37] rounded-full transition-all" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ContractorPhotosView() {
  const [photos, setPhotos] = useState([]);
  const fileRef = useRef(null);
  const handleUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const newPhotos = files.map(f => ({ url: URL.createObjectURL(f), name: f.name, date: new Date().toLocaleDateString() }));
    setPhotos(prev => [...prev, ...newPhotos]);
    toast.success(`${files.length} photo(s) added`);
  };
  return (
    <div>
      <ViewHeader title="Progress Photos" subtitle="Document project progress with photos" />
      <div
        onClick={() => fileRef.current?.click()}
        className="border-2 border-dashed border-white/[0.08] rounded-xl p-10 text-center cursor-pointer hover:border-[#D4AF37]/30 transition-all mb-6"
        data-testid="photo-upload-area"
      >
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
        <Camera className="w-10 h-10 text-white/20 mx-auto mb-3" />
        <p className="text-white/40 text-sm">Click to upload progress photos</p>
        <p className="text-white/20 text-xs mt-1">JPG, PNG • Multiple files supported</p>
      </div>
      {photos.length > 0 ? (
        <div className="grid grid-cols-3 gap-3">
          {photos.map((p, i) => (
            <div key={i} className="aspect-video bg-white/[0.04] border border-white/[0.08] rounded-lg overflow-hidden relative group">
              <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-white text-[10px] truncate">{p.name}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <PortalEmptyState icon={ImagePlus} title="No Photos Yet" desc="Upload photos to document your work progress" />
      )}
    </div>
  );
}

function ContractorScheduleView({ activeJobs }) {
  const today = new Date();
  const scheduled = activeJobs.map((job, i) => ({
    ...job,
    startDate: new Date(job.created_at || today),
    endDate: new Date((job.created_at || today.toISOString()).replace('Z', '')),
    daysLeft: job.timeline_days ? Math.max(0, job.timeline_days - Math.floor((Date.now() - new Date(job.created_at || today).getTime()) / 86400000)) : '--'
  }));
  return (
    <div>
      <ViewHeader title="Project Schedule" subtitle="Timeline view of all active and upcoming jobs" />
      {scheduled.length === 0 ? (
        <PortalEmptyState icon={Calendar} title="No Scheduled Projects" desc="Active projects will appear here with their timelines" />
      ) : (
        <div className="space-y-3">
          {scheduled.map((job, idx) => (
            <div key={idx} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 flex items-center gap-5">
              <div className="w-14 h-14 bg-[#D4AF37]/[0.06] border border-[#D4AF37]/20 rounded-xl flex flex-col items-center justify-center flex-shrink-0">
                <span className="text-[#D4AF37] font-bold text-lg leading-none">{new Date(job.created_at || today).getDate()}</span>
                <span className="text-[#D4AF37]/60 text-[8px] uppercase">{new Date(job.created_at || today).toLocaleString('default', { month: 'short' })}</span>
              </div>
              <div className="flex-1">
                <p className="text-white font-medium text-sm">{job.property_address || 'Active Project'}</p>
                <p className="text-white/40 text-xs">{job.job_type || 'General Contracting'}</p>
                <div className="flex items-center gap-3 mt-2 text-[10px] text-white/30">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{job.daysLeft} days remaining</span>
                  <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{formatCurrency(job.contract_amount || job.budget || 0)}</span>
                </div>
              </div>
              <span className={`text-[10px] px-2.5 py-1 border rounded uppercase font-semibold ${
                job.executionStage === 'in_progress' ? 'text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/20' : 'text-blue-400 bg-blue-500/10 border-blue-500/20'
              }`}>
                {job.executionStage?.replace('_', ' ') || 'Active'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContractorEarningsView({ stats, myBids, activeJobs }) {
  const totalEarnings = stats?.total_earnings || 0;
  const completed = stats?.jobs_completed || 0;
  const pending = activeJobs.reduce((s, j) => s + (j.contract_amount || j.budget || 0), 0);
  return (
    <div>
      <ViewHeader title="Earnings" subtitle="Revenue breakdown and payment tracking" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total Earned', value: formatCurrency(totalEarnings), color: 'text-[#00FF88]', border: 'border-emerald-500/20' },
          { label: 'Jobs Completed', value: completed, color: 'text-[#D4AF37]', border: 'border-[#D4AF37]/20' },
          { label: 'In Progress Value', value: formatCurrency(pending), color: 'text-blue-400', border: 'border-blue-500/20' },
        ].map(({ label, value, color, border }) => (
          <div key={label} className={`bg-[#0A0A0A] border ${border} rounded-xl p-5 text-center`}>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-white/40 text-xs mt-1">{label}</p>
          </div>
        ))}
      </div>
      <PartnerPaymentHistory partnerType="contractor" apiPrefix="/api/contractor" paymentTypeLabel="Draw Payments" />
    </div>
  );
}

function ContractorAnalyticsView({ stats, availableJobs, myBids }) {
  const winRate = myBids.length > 0 ? Math.round(((stats?.won_bids || 0) / myBids.length) * 100) : 0;
  const metrics = [
    { label: 'Total Bids', value: stats?.total_bids || 0, icon: FileText, color: 'text-blue-400' },
    { label: 'Won Bids', value: stats?.won_bids || 0, icon: CheckCircle, color: 'text-[#00FF88]' },
    { label: 'Win Rate', value: `${winRate}%`, icon: TrendingUp, color: 'text-[#D4AF37]' },
    { label: 'Avg Rating', value: stats?.avg_rating?.toFixed(1) || '--', icon: Star, color: 'text-amber-400' },
  ];
  return (
    <div>
      <ViewHeader title="Performance Analytics" subtitle="Your contractor performance metrics" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {metrics.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 text-center">
            <Icon className={`w-6 h-6 mx-auto mb-2 ${color}`} />
            <p className={`text-2xl font-bold text-white`}>{value}</p>
            <p className="text-white/30 text-xs mt-1">{label}</p>
          </div>
        ))}
      </div>
      <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
        <h3 className="text-white/60 text-sm font-medium mb-4">Bid Pipeline Status</h3>
        <div className="space-y-3">
          {['pending', 'accepted', 'rejected'].map(status => {
            const count = myBids.filter(b => (b.status || 'pending') === status).length;
            const pct = myBids.length > 0 ? (count / myBids.length) * 100 : 0;
            const colors = { pending: '#FF9F0A', accepted: '#00FF88', rejected: '#FF3B30' };
            return (
              <div key={status}>
                <div className="flex justify-between text-xs text-white/40 mb-1">
                  <span className="capitalize">{status}</span>
                  <span>{count} ({Math.round(pct)}%)</span>
                </div>
                <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: colors[status] }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SettingsProfileView({ profile, fields, onSave }) {
  const [form, setForm] = useState(profile || {});
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    try { if (onSave) await onSave(form); toast.success("Profile updated"); }
    catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <ViewHeader title="Configuration" subtitle="Manage your portal profile and settings" />
      <div className="max-w-lg bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-6 space-y-4">
        {fields.map(({ key, label, type = 'text' }) => (
          <div key={key}>
            <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">{label}</label>
            {type === 'textarea' ? (
              <textarea
                value={form[key] || ''}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                rows={3}
                className="w-full bg-white/[0.03] border border-white/[0.08] rounded px-3 py-2 text-sm text-white resize-none focus:outline-none focus:border-[#D4AF37]/50"
              />
            ) : (
              <input
                type={type}
                value={form[key] || ''}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                className="w-full bg-white/[0.03] border border-white/[0.08] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/50"
              />
            )}
          </div>
        ))}
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-2.5 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold text-sm rounded transition-all disabled:opacity-50"
          data-testid="save-settings-btn"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

// ─── Contractor Nav Sections ──────────────────────────────────────────────────
const CONTRACTOR_NAV = [
  {
    title: "Operations",
    items: [
      { icon: Target, label: "Command Center", view: "dashboard" },
      { icon: Briefcase, label: "Available Jobs", view: "jobs" },
      { icon: FileText, label: "My Bids", view: "bids" },
    ]
  },
  {
    title: "Execution",
    items: [
      { icon: Hammer, label: "Active Projects", view: "active" },
      { icon: Camera, label: "Progress Photos", view: "photos" },
      { icon: CalendarDays, label: "Deal Calendar", url: "/calendar?from=contractor" },
    ]
  },
  {
    title: "Tools",
    items: [
      { icon: Wallet, label: "Bid Builder", view: "bid-builder" },
      { icon: DollarSign, label: "Invoices", view: "invoices" },
    ]
  },
  {
    title: "Intelligence",
    items: [
      { icon: DollarSign, label: "Earnings", view: "earnings" },
      { icon: TrendingUp, label: "Performance", view: "analytics" },
      { icon: FileSignature, label: "Documents", view: "documents" },
      { icon: Settings, label: "Configuration", view: "settings" },
    ]
  }
];

// Verification Banner
function VerificationBanner({ contractor }) {
  if (!contractor || contractor.verification_status === "verified") return null;
  
  const status = contractor.verification_status || "pending";
  
  return (
    <div className={`border-b px-6 py-3 flex items-center gap-4 ${
      status === "rejected" ? "bg-red-500/[0.08] border-red-500/20" : "bg-amber-500/[0.08] border-amber-500/20"
    }`}>
      {status === "rejected" ? (
        <XCircle className="w-5 h-5 text-[#FF3B30] flex-shrink-0" />
      ) : (
        <Clock className="w-5 h-5 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] flex-shrink-0" />
      )}
      <div className="flex-1">
        <p className={`font-medium text-sm ${status === "rejected" ? "text-[#FF3B30]" : "text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]"}`}>
          {status === "rejected" ? "Verification Rejected" : "Verification Pending"}
        </p>
        <p className="text-white/40 text-xs mt-0.5">
          {status === "rejected" 
            ? "Please contact support to resolve verification issues"
            : "Your profile is under review. You'll be notified once verified."}
        </p>
      </div>
    </div>
  );
}

export default function ContractorCommandCenter({ contractor, onLogout, user }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [systemStatus, setSystemStatus] = useState('stable');
  const [isAdminViewing, setIsAdminViewing] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  const [notifyCounts, setNotifyCounts] = useState({});
  const [bidBuilderData, setBidBuilderData] = useState([]);
  const [invoiceData, setInvoiceData] = useState([]);
  const canvasRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // If admin is viewing without contractor data, show demo mode immediately
    if (user?.is_admin && !contractor) {
      setIsAdminViewing(true);
      setStats({ total_bids: 0, won_bids: 0, active_jobs: 0, total_earnings: 0 });
      setAvailableJobs([]);
      setMyBids([]);
      setActiveJobs([]);
      setLoading(false);
      return;
    }
    
    fetchData();
  }, [user, contractor]);

  const fetchData = async () => {
    try {
      const [statsRes, jobsRes, bidsRes, activeRes, bidsToolRes, invoicesRes, notifyRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/contractor/dashboard/stats`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/contractor/jobs/available`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/contractor/bids`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/contractor/jobs/active`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/portal-tools/bids`, { withCredentials: true }).catch(() => ({ data: { bids: [] } })),
        axios.get(`${BACKEND_URL}/api/portal-tools/invoices`, { withCredentials: true }).catch(() => ({ data: { invoices: [] } })),
        axios.get(`${BACKEND_URL}/api/portal-tools/notification-counts`, { withCredentials: true }).catch(() => ({ data: { counts: {} } })),
      ]);
      
      setStats(statsRes.data);
      setIsAdminViewing(false);
      setAvailableJobs((jobsRes.data || []).map(j => ({ ...j, executionStage: 'available' })));
      setMyBids((bidsRes.data || []).map(b => ({ ...b, executionStage: 'bidding' })));
      setActiveJobs((activeRes.data || []).map(a => ({ ...a, executionStage: 'in_progress' })));
      setBidBuilderData(bidsToolRes.data?.bids || []);
      setInvoiceData(invoicesRes.data?.invoices || []);
      setNotifyCounts(notifyRes.data?.counts || {});

      const jobCount = jobsRes.data?.length || 0;
      if (jobCount > 10) setSystemStatus('bottleneck');
      else if (jobCount > 5) setSystemStatus('alert');
      else setSystemStatus('stable');
      
    } catch (error) {
      if (user?.is_admin) {
        setIsAdminViewing(true);
        setStats({ total_bids: 0, won_bids: 0, active_jobs: 0, total_earnings: 0 });
        setAvailableJobs([]);
        setMyBids([]);
        setActiveJobs([]);
      } else if (error.response?.status !== 403) {
        toast.error("Failed to load dashboard data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/contractor/auth/logout`, {}, { withCredentials: true });
      onLogout?.();
      navigate("/");
    } catch (error) {
      onLogout?.();
      navigate("/");
    }
  };

  // Calculate stage metrics
  const getStageMetrics = () => {
    return JOB_STAGES.map(stage => {
      let items = [];
      if (stage.key === 'available') items = availableJobs;
      else if (stage.key === 'bidding') items = myBids;
      else if (stage.key === 'in_progress') items = activeJobs;
      else if (stage.key === 'completed') items = stats?.completed_jobs || [];
      
      const count = Array.isArray(items) ? items.length : 0;
      const volume = Array.isArray(items) ? items.reduce((sum, item) => sum + (item.budget || item.bid_amount || 0), 0) : 0;
      
      return { ...stage, count, volume };
    });
  };

  // Global metrics
  const globalMetrics = {
    availableJobs: availableJobs.length,
    pendingBids: myBids.length,
    activeProjects: activeJobs.length,
    totalEarnings: stats?.total_earnings || 0
  };

  const stageMetrics = getStageMetrics();
  const allJobs = [...availableJobs.slice(0, 3), ...activeJobs.slice(0, 2)];

  // Canvas animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    let particles = [];
    let animationId;

    const createParticle = () => {
      const stageIdx = Math.floor(Math.random() * (JOB_STAGES.length - 1));
      return {
        x: (stageIdx + 0.5) * (canvas.width / JOB_STAGES.length),
        y: canvas.height / 2 + (Math.random() - 0.5) * 30,
        targetX: (stageIdx + 1.5) * (canvas.width / JOB_STAGES.length),
        progress: 0,
        speed: 0.003 + Math.random() * 0.003,
        color: JOB_STAGES[stageIdx].color
      };
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      JOB_STAGES.forEach((stage, idx) => {
        if (idx < JOB_STAGES.length - 1) {
          const x1 = (idx + 0.5) * (canvas.width / JOB_STAGES.length);
          const x2 = (idx + 1.5) * (canvas.width / JOB_STAGES.length);
          ctx.beginPath();
          ctx.moveTo(x1, canvas.height / 2);
          ctx.lineTo(x2, canvas.height / 2);
          ctx.strokeStyle = 'rgba(255,255,255,0.02)';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });

      if (Math.random() < 0.02 && particles.length < 12) {
        particles.push(createParticle());
      }

      particles = particles.filter(p => {
        p.progress += p.speed;
        if (p.progress >= 1) return false;
        
        const x = p.x + (p.targetX - p.x) * p.progress;
        const y = p.y + Math.sin(p.progress * Math.PI) * 8;
        
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.4 * (1 - p.progress);
        ctx.fill();
        ctx.globalAlpha = 1;
        return true;
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(animationId);
  }, [availableJobs, myBids, activeJobs]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <PortalSidebar
        navSections={CONTRACTOR_NAV}
        activeView={activeView}
        setActiveView={setActiveView}
        homeLink="/contractor"
        portal="Contractor"
        entity={contractor}
        entityInitial="C"
        entitySubtitle={contractor?.specialties?.[0] || 'General Contractor'}
        accent="gold"
        onLogout={handleLogout}
        notifyCounts={notifyCounts}
        currentRole="contractor"
        userRoles={user?.extra_roles || []}
        isAdmin={user?.is_admin || false}
      />
      
      <main className="ml-16 lg:ml-64 transition-all">
        {/* Verification Banner */}
        <VerificationBanner contractor={contractor} />
        
        {/* System Overview Strip */}
        <header className="border-b border-white/[0.06] bg-[#0A0A0A]/80 backdrop-blur-sm">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div>
                  <h1 className="text-lg font-semibold tracking-tight">
                    Contractor <span className="text-[#D4AF37]">Command</span>
                  </h1>
                  <p className="text-[9px] text-white/30 uppercase tracking-[0.15em]">Project Operations</p>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/[0.08] border border-emerald-500/20 rounded">
                    <div className="relative w-2 h-2">
                      <div className="absolute inset-0 bg-emerald-400 rounded-full" />
                      <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-75" />
                    </div>
                    <span className="text-[10px] font-semibold text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] uppercase tracking-wider">Live</span>
                  </div>
                  <SystemStatusBadge status={systemStatus} />
                </div>
              </div>

              <div className="flex items-center gap-8">
                <GlobalMetric label="Available Jobs" value={globalMetrics.availableJobs} />
                <div className="w-px h-10 bg-white/[0.04]" />
                <GlobalMetric label="Pending Bids" value={globalMetrics.pendingBids} />
                <div className="w-px h-10 bg-white/[0.04]" />
                <GlobalMetric label="Active Projects" value={globalMetrics.activeProjects} />
                <div className="w-px h-10 bg-white/[0.04]" />
                <GlobalMetric label="Total Earnings" value={formatCurrency(globalMetrics.totalEarnings)} />
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-xs text-white/50 font-mono">
                    {currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </p>
                  <p className="text-lg font-mono font-bold text-white">
                    {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <Button 
                  onClick={() => setActiveView('jobs')}
                  className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold h-10 px-5"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Browse Jobs
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Execution Flow Visualization */}
        <section className="border-b border-white/[0.06] relative">
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />
          <div className="relative z-10 flex">
            {stageMetrics.map((stage) => (
              <ExecutionStage
                key={stage.key}
                stage={stage}
                count={stage.count}
                volume={stage.volume}
                isActive={activeStage === stage.key}
                onClick={() => setActiveStage(activeStage === stage.key ? null : stage.key)}
              />
            ))}
          </div>
        </section>

        {/* Stripe Connect Banner */}
        <div className="px-6 pt-4">
          <StripeConnectBanner 
            partnerType="contractor" 
            apiPrefix="/api/contractor"
            compact={true}
          />
        </div>

        {/* Main Content Grid */}
        <div className="p-6">
          {(() => {
            switch(activeView) {
              case 'jobs': return <ContractorJobsListView availableJobs={availableJobs} myBids={myBids} setActiveView={setActiveView} />;
              case 'bids': return <ContractorBidsView myBids={myBids} />;
              case 'active': return <ContractorActiveView activeJobs={activeJobs} />;
              case 'photos': return <ContractorPhotosView />;
              case 'schedule': return <ContractorScheduleView activeJobs={activeJobs} />;
              case 'earnings': return <ContractorEarningsView stats={stats} myBids={myBids} activeJobs={activeJobs} />;
              case 'analytics': return <ContractorAnalyticsView stats={stats} availableJobs={availableJobs} myBids={myBids} />;
              case 'bid-builder': return <BidBuilder bids={bidBuilderData} onBidCreated={b => setBidBuilderData(p => [b, ...p])} />;
              case 'invoices': return <InvoiceManager invoices={invoiceData} onCreated={inv => setInvoiceData(p => [inv, ...p])} />;
              case 'documents': return <PortalDocumentHub portalType="contractor" />;
              case 'settings': return (
                <SettingsProfileView
                  profile={contractor}
                  fields={[
                    { key: 'company_name', label: 'Company Name' },
                    { key: 'name', label: 'Contact Name' },
                    { key: 'email', label: 'Email', type: 'email' },
                    { key: 'phone', label: 'Phone' },
                    { key: 'license_number', label: 'License Number' },
                    { key: 'service_area', label: 'Service Area' },
                    { key: 'bio', label: 'About / Bio', type: 'textarea' },
                  ]}
                />
              );
              default: return (
                <>
                  {/* Exchange Opportunities Widget — top priority */}
                  <ExchangeWidget role="contractor" user={user} className="mb-6" />

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    
                    {/* Left: Job Feed */}
                    <div className="lg:col-span-2 space-y-4">
                      <div className="flex items-center justify-between">
                        <h2 className="text-xs font-semibold text-white/40 uppercase tracking-wider">Job Pipeline</h2>
                        {activeStage && (
                          <button onClick={() => setActiveStage(null)} className="text-[10px] text-white/40 hover:text-white">Clear Filter</button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {allJobs.length > 0 ? (
                          allJobs.map((job, idx) => (
                            <JobRow key={job.job_id || job.bid_id || idx} job={job} onClick={() => setActiveView('jobs')} onBid={() => toast.info("Submit bid — go to Available Jobs")} />
                          ))
                        ) : (
                          <div className="text-center py-16 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06]">
                            <Briefcase className="w-12 h-12 text-white/30 mx-auto mb-3" />
                            <p className="text-white/40">No jobs available</p>
                            <p className="text-white/30 text-sm mt-2">Check back soon for new opportunities</p>
                          </div>
                        )}
                      </div>
                      {allJobs.length > 0 && (
                        <button onClick={() => setActiveView('jobs')} className="flex items-center justify-center gap-2 py-3 text-sm text-white/50 hover:text-[#D4AF37] transition-all duration-300 w-full">
                          View All Jobs <ArrowRight className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Right: Quick Deploy Panel */}
                    <div className="space-y-4">
                      <h2 className="text-xs font-semibold text-white/40 uppercase tracking-wider">Quick Deploy</h2>
                      <div className="space-y-2">
                        <QuickDeployAction icon={Briefcase} label="Browse Jobs" description="View available opportunities" variant="primary" onClick={() => setActiveView('jobs')} />
                        <QuickDeployAction icon={FileText} label="My Bids" description="Track submitted bids" onClick={() => setActiveView('bids')} />
                        <QuickDeployAction icon={Camera} label="Upload Progress" description="Add project photos" onClick={() => setActiveView('photos')} />
                        <QuickDeployAction icon={TrendingUp} label="Analytics" description="Performance metrics" onClick={() => setActiveView('analytics')} />
                      </div>
                      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] p-4 mt-4">
                        <h3 className="text-[10px] font-semibold text-white/30 uppercase tracking-wider mb-3">Performance Summary</h3>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-white/50">Jobs Completed</span>
                            <span className="text-sm font-mono text-[#D4AF37]">{stats?.jobs_completed || 0}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-white/50">Total Earnings</span>
                            <span className="text-sm font-mono font-bold text-white">{formatCurrency(globalMetrics.totalEarnings)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-white/50">Avg Rating</span>
                            <span className="text-sm font-mono font-bold text-white flex items-center gap-1">
                              <Star className="w-3 h-3 text-[#D4AF37]" />
                              {stats?.avg_rating?.toFixed(1) || '--'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8">
                    <PartnerPaymentHistory partnerType="contractor" apiPrefix="/api/contractor" paymentTypeLabel="Draw Payments" />
                  </div>
                </>
              );
            }
          })()}
        </div>
      </main>
    </div>
  );
}
